/**
 * MoneyByPurposeCard — Phase 86.D. "What your money is for".
 *
 * Replaces the "what accounts do I have?" framing with "what is this money
 * intended to DO?" — a more human question than an asset-mix donut.
 *
 * Every bucket is mutually exclusive and the asset rows sum to the same total
 * assets figure shown in the hero, because the backend splits each resource
 * between what goals claim and what remains. The same rupee is never counted in
 * two buckets, and if the user has over-promised a balance we say so rather
 * than letting two goals both look funded.
 */
import React from 'react';
import { AlertTriangle, ArrowRight, ShieldCheck } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { formatMoney } from '@/lib/format';

const BUCKET_TONE = {
  everyday:          '#3D7A63',
  emergency:         '#2F6B57',
  assigned_to_goals: '#5B8C7B',
  long_term:         '#A07830',
  unassigned:        '#B8B8B0',
  debt:              '#A84E38',
};

export default function MoneyByPurposeCard({ data, loading, onManage }) {
  if (loading && !data) {
    return (
      <section className="bg-card rounded-xl border border-border shadow-sm p-5" data-testid="money-purpose-loading">
        <Skeleton className="h-4 w-44 mb-4" />
        {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-7 w-full mb-2" />)}
      </section>
    );
  }
  if (!data) return null;

  const base = data.base_currency;
  // Show every asset bucket, including the empty ones. A zero next to
  // "Emergency reserves" tells the user the concept exists and they have
  // nothing in it — hiding the row hides the gap.
  const assetRows = (data.rows || []).filter((r) => !r.is_debt);
  const debtRow = (data.rows || []).find((r) => r.is_debt && r.amount > 0);
  const total = data.asset_total || 0;
  const em = data.emergency || {};
  const over = data.over_allocated || [];

  return (
    <section
      className="bg-card rounded-xl border border-border shadow-sm overflow-hidden"
      data-testid="money-purpose-card"
    >
      <header className="px-5 pt-5 pb-3 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-charcoal">What your money is for</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Purpose and goal assignments across cash and investments.
          </p>
        </div>
        <button
          type="button"
          onClick={onManage}
          className="text-xs font-medium text-primary hover:underline shrink-0 inline-flex items-center gap-1"
          data-testid="money-purpose-manage"
        >
          Manage assignments <ArrowRight className="h-3 w-3" />
        </button>
      </header>

      <div className="px-5 pb-5">
        {assetRows.every((r) => r.amount === 0) ? (
          <p className="text-xs text-muted-foreground py-4" data-testid="money-purpose-empty">
            Once you tag what your accounts are for, this becomes a breakdown of
            what your money is actually doing.
          </p>
        ) : (
          <ul className="space-y-2.5" data-testid="money-purpose-rows">
            {assetRows.map((r) => {
              const pct = total ? (r.amount / total) * 100 : 0;
              return (
                <li key={r.key} data-testid={`money-purpose-row-${r.key}`}>
                  <div className="flex items-baseline justify-between gap-3 mb-1">
                    <span className={`text-sm ${r.amount > 0 ? 'text-charcoal' : 'text-muted-foreground'}`}>
                      {r.label}
                    </span>
                    <span className={`num text-sm tabular-nums ${
                      r.amount > 0 ? 'font-medium text-charcoal' : 'text-muted-foreground'}`}>
                      {formatMoney(r.amount, base)}
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full bg-muted/60 overflow-hidden">
                    {r.amount > 0 && (
                      <div
                        className="h-full rounded-full transition-[width] duration-500"
                        style={{ width: `${Math.max(pct, 1)}%`, backgroundColor: BUCKET_TONE[r.key] }}
                      />
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {debtRow && (
          <div className="flex items-baseline justify-between gap-3 mt-4 pt-3 border-t border-border/60"
               data-testid="money-purpose-row-debt">
            <span className="text-sm text-charcoal">{debtRow.label}</span>
            <span className="num text-sm font-medium tabular-nums" style={{ color: BUCKET_TONE.debt }}>
              −{formatMoney(debtRow.amount, base)}
            </span>
          </div>
        )}

        {/* Emergency reserve expressed in months, with an honest caveat about
            what the baseline is measuring. */}
        {em.available && em.monthly_baseline > 0 && (
          <p className="text-xs text-charcoal mt-4 pt-3 border-t border-border/60 flex items-start gap-2"
             data-testid="money-purpose-months">
            <ShieldCheck className="h-3.5 w-3.5 mt-0.5 shrink-0" style={{ color: BUCKET_TONE.emergency }} />
            <span>
              {em.amount > 0 && em.months_protected != null ? (
                <>
                  Your reserve covers about{' '}
                  <strong className="font-medium">{em.months_protected} months</strong>{' '}
                  at {formatMoney(em.monthly_baseline, base)}/month.
                </>
              ) : (
                <>
                  You spend about{' '}
                  <strong className="font-medium">{formatMoney(em.monthly_baseline, base)}/month</strong>,
                  and nothing is tagged as an emergency reserve yet.
                </>
              )}
              <span className="text-muted-foreground">
                {' '}Based on all spending over {em.window || 'the last 3 months'} — not
                essentials only, so treat it as a floor.
              </span>
            </span>
          </p>
        )}

        {data.untagged_cash > 0 && (
          <p className="text-[11px] text-muted-foreground mt-3" data-testid="money-purpose-untagged">
            {formatMoney(data.untagged_cash, base)} of cash has no purpose set yet.
          </p>
        )}

        {over.length > 0 && (
          <div
            className="mt-4 rounded-lg border px-3.5 py-3 flex items-start gap-2.5"
            style={{ borderColor: '#A84E3840', backgroundColor: '#A84E380F' }}
            data-testid="money-purpose-over-allocated"
          >
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" style={{ color: '#A84E38' }} />
            <div className="text-xs leading-relaxed">
              <p className="font-semibold text-charcoal">
                {over.length === 1 ? 'One balance is' : `${over.length} balances are`} promised
                to more than it holds.
              </p>
              {over.slice(0, 2).map((o) => (
                <p key={o.resource_id} className="text-muted-foreground mt-0.5">
                  {o.name}: {formatMoney(o.assigned, o.currency)} assigned across{' '}
                  {o.goals.length} goal{o.goals.length === 1 ? '' : 's'} but only{' '}
                  {formatMoney(o.value, o.currency)} available — over by{' '}
                  {formatMoney(o.excess, o.currency)}.
                </p>
              ))}
            </div>
          </div>
        )}

        {data.reconciles === false && (
          <p className="text-[11px] mt-3" style={{ color: '#8A5A0C' }} data-testid="money-purpose-mismatch">
            These buckets don&rsquo;t currently add up to your total assets
            ({formatMoney(data.assets, base)}). We&rsquo;re showing it rather than
            hiding it — please report this.
          </p>
        )}
      </div>
    </section>
  );
}
